import { QueryClient } from "@tanstack/react-query";

import getAuthClient from "@skilitics/designer-auth";
import { getIsAuthenticated } from "@skilitics/designer-auth/state";

import { isFetchOrAxiosNetworkError } from "../errors/isFetchOrAxiosNetworkError.ts";

const throwOnError = (error: unknown) => {
  // forward to the global error handler, so e.g. Sentry can handle it
  reportError(error);
  // Should mean the useQuery() etc. will not throw an error,
  // but this doesn't seem to be the case in some situations?
  return false;
};

// Export a singleton at least for now, to avoid some janky state issues in
// the app router when pre-fetching data.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // By default, don't fetch unless the user is logged in.
      enabled: getIsAuthenticated,
      // Retrying the default 4 times mostly just causes error spam on local,
      // but we also mostly[1] suppress network errors which are the ones we want
      // to give a retry anyway...
      // Don't forget to check the attempt number!
      // [1] we show a toast in the corner, but not the sentry dialog.
      retry(failureCount, error) {
        return failureCount < 4 && isFetchOrAxiosNetworkError(error);
      },
      // This is necessary to avoid double-fetches if prefetch returns an error.
      retryOnMount: isFetchOrAxiosNetworkError,
      // Otherwise successful data shouldn't be refetched unless specifically
      // requested.
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      throwOnError,
    },
    mutations: {
      throwOnError,
    },
  },
});

// logout or other workspace/auth context switch should clear all current state.
const authClient = getAuthClient();
let lastWorkspace = authClient.apiToken?.workspace;
authClient.subscribe(() => {
  const newWorkspace = authClient.apiToken?.workspace;
  if (lastWorkspace !== newWorkspace) {
    lastWorkspace = newWorkspace;
    // There's a bunch of different options here that are not at all well documented.
    if (authClient.stage !== "authenticated") {
      // When we're logging out clear any queries that might have cached data.
      queryClient.clear();
    } else {
      // Otherwise we don't want to kill any existing queries, just the cached data.
      // Using refetchQueries instead of invalidateQueries here so we get a loading
      // state instead of it being stale-while-revalidate which could be confusing
      // for a workspace switch.
      void queryClient.refetchQueries();
    }
  }
});
